import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import Footer from './Footer';
import {
    LuCheck,
    LuX,
    LuCrown,
    LuShield,
    LuHeadphones,
    LuInfinity,
    LuDownload,
    LuChartArea,
    LuCloud,
    LuSparkles
} from 'react-icons/lu';

function Pricing() {
    const { t } = useLanguage();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [billingCycle, setBillingCycle] = useState('monthly');

    const plans = [
        {
            id: 'free',
            name: t('pricing.freePlan'),
            price: 0,
            period: t('pricing.forever'),
            description: t('pricing.freeDescription'),
            features: [
                { text: t('pricing.features.calculations5'), included: true },
                { text: t('pricing.features.basicCharts'), included: true },
                { text: t('pricing.features.historyLocal'), included: true },
                { text: t('pricing.features.exportPdf'), included: false },
                { text: t('pricing.features.cloudSync'), included: false },
                { text: t('pricing.features.advancedCharts'), included: false },
                { text: t('pricing.features.noAds'), included: false },
                { text: t('pricing.features.prioritySupport'), included: false },
            ],
            buttonText: t('pricing.currentPlan'),
            buttonVariant: 'secondary',
            popular: false
        },
        {
            id: 'premium',
            name: t('pricing.premiumPlan'),
            price: billingCycle === 'monthly' ? 49.99 : 399.99,
            originalPrice: billingCycle === 'yearly' ? 599.88 : null,
            period: billingCycle === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear'),
            description: t('pricing.premiumDescription'),
            features: [
                { text: t('pricing.features.unlimitedCalculations'), included: true },
                { text: t('pricing.features.allCharts'), included: true },
                { text: t('pricing.features.cloudSync'), included: true },
                { text: t('pricing.features.exportAll'), included: true },
                { text: t('pricing.features.advancedAnalysis'), included: true },
                { text: t('pricing.features.noAds'), included: true },
                { text: t('pricing.features.prioritySupport'), included: true },
                { text: t('pricing.features.apiAccess'), included: true },
            ],
            buttonText: t('pricing.subscribe'),
            buttonVariant: 'primary',
            popular: true,
            badge: billingCycle === 'yearly' ? t('pricing.save33') : null
        },
        {
            id: 'enterprise',
            name: t('pricing.enterprisePlan'),
            price: null,
            period: t('pricing.custom'),
            description: t('pricing.enterpriseDescription'),
            features: [
                { text: t('pricing.features.everythingPremium'), included: true },
                { text: t('pricing.features.dedicatedSupport'), included: true },
                { text: t('pricing.features.customIntegrations'), included: true },
                { text: t('pricing.features.sla'), included: true },
                { text: t('pricing.features.onboarding'), included: true },
                { text: t('pricing.features.multiUser'), included: true },
                { text: t('pricing.features.invoicing'), included: true },
                { text: t('pricing.features.customReports'), included: true },
            ],
            buttonText: t('pricing.contactSales'),
            buttonVariant: 'outline',
            popular: false
        }
    ];

    const handleSelectPlan = (planId) => {
        if (!user) {
            navigate('/register');
            return;
        }

        if (planId === 'enterprise') {
            navigate('/support');
            return;
        }

        // TODO: Implement payment flow
        console.log('Selected plan:', planId);
    };

    return (
        <div className="app-container">
            <Navbar />

            <main className="main-content">
                <div className="pricing-page">
                    {/* Header */}
                    <div className="pricing-header home-header">
                        <h1 className="pricing-title">{t('pricing.title')}</h1>

                        {/* Billing Toggle */}
                        <div className="billing-toggle">
                            <button
                                className={`billing-option ${billingCycle === 'monthly' ? 'active' : ''}`}
                                onClick={() => setBillingCycle('monthly')}
                            >
                                {t('pricing.monthly')}
                            </button>
                            <button
                                className={`billing-option ${billingCycle === 'yearly' ? 'active' : ''}`}
                                onClick={() => setBillingCycle('yearly')}
                            >
                                {t('pricing.yearly')}
                                <span className="billing-badge">{t('pricing.save33')}</span>
                            </button>
                        </div>
                    </div>

                    {/* Plans Grid */}
                    <div className="pricing-grid">
                        {plans.map((plan) => (
                            <div
                                key={plan.id}
                                className={`pricing-card ${plan.popular ? 'popular' : ''}`}
                            >
                                {plan.popular && (
                                    <div className="popular-badge">
                                        <LuCrown /> {t('pricing.mostPopular')}
                                    </div>
                                )}

                                <div className="plan-header">
                                    <h3 className="plan-name">{plan.name}</h3>
                                    <p className="plan-description">{plan.description}</p>
                                </div>

                                <div className="plan-price">
                                    {plan.price !== null ? (
                                        <>
                                            {plan.originalPrice && (
                                                <span className="original-price">
                                                    ₺{plan.originalPrice}
                                                </span>
                                            )}
                                            <span className="price-amount">
                                                {plan.price === 0 ? t('pricing.free') : `₺${plan.price}`}
                                            </span>
                                            <span className="price-period">{plan.period}</span>
                                        </>
                                    ) : (
                                        <span className="price-custom">{t('pricing.contactUs')}</span>
                                    )}
                                </div>

                                <ul className="plan-features">
                                    {plan.features.map((feature, index) => (
                                        <li key={index} className={feature.included ? 'included' : 'excluded'}>
                                            {feature.included ? (
                                                <LuCheck className="feature-icon included" />
                                            ) : (
                                                <LuX className="feature-icon excluded" />
                                            )}
                                            {feature.text}
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    className={`btn btn-${plan.buttonVariant} btn-plan`}
                                    onClick={() => handleSelectPlan(plan.id)}
                                    disabled={plan.id === 'free' && user?.subscription?.plan === 'free'}
                                >
                                    {plan.buttonText}
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Features Comparison */}
                    <section className="features-section">
                        <h2 className="section-title">{t('pricing.whyPremium')}</h2>
                        <div className="features-grid">
                            <div className="feature-card">
                                <div className="feature-icon-wrapper">
                                    <LuInfinity />
                                </div>
                                <h3>{t('pricing.featureCards.unlimited.title')}</h3>
                                <p>{t('pricing.featureCards.unlimited.description')}</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon-wrapper">
                                    <LuChartArea />
                                </div>
                                <h3>{t('pricing.featureCards.charts.title')}</h3>
                                <p>{t('pricing.featureCards.charts.description')}</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon-wrapper">
                                    <LuCloud />
                                </div>
                                <h3>{t('pricing.featureCards.cloud.title')}</h3>
                                <p>{t('pricing.featureCards.cloud.description')}</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon-wrapper">
                                    <LuDownload />
                                </div>
                                <h3>{t('pricing.featureCards.export.title')}</h3>
                                <p>{t('pricing.featureCards.export.description')}</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon-wrapper">
                                    <LuShield />
                                </div>
                                <h3>{t('pricing.featureCards.security.title')}</h3>
                                <p>{t('pricing.featureCards.security.description')}</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon-wrapper">
                                    <LuHeadphones />
                                </div>
                                <h3>{t('pricing.featureCards.support.title')}</h3>
                                <p>{t('pricing.featureCards.support.description')}</p>
                            </div>
                        </div>
                    </section>

                    {/* FAQ Section */}
                    <section className="pricing-faq">
                        <h2 className="section-title">{t('pricing.faqTitle')}</h2>
                        <div className="faq-grid">
                            <div className="faq-item">
                                <h4>{t('pricing.faq.trial.question')}</h4>
                                <p>{t('pricing.faq.trial.answer')}</p>
                            </div>
                            <div className="faq-item">
                                <h4>{t('pricing.faq.cancel.question')}</h4>
                                <p>{t('pricing.faq.cancel.answer')}</p>
                            </div>
                            <div className="faq-item">
                                <h4>{t('pricing.faq.payment.question')}</h4>
                                <p>{t('pricing.faq.payment.answer')}</p>
                            </div>
                            <div className="faq-item">
                                <h4>{t('pricing.faq.refund.question')}</h4>
                                <p>{t('pricing.faq.refund.answer')}</p>
                            </div>
                        </div>
                    </section>

                    {/* CTA Section */}
                    <section className="pricing-cta">
                        <div className="cta-content">
                            <LuSparkles className="cta-icon" />
                            <h2>{t('pricing.ctaTitle')}</h2>
                            <p>{t('pricing.ctaDescription')}</p>
                            <button
                                className="btn btn-primary btn-lg"
                                onClick={() => handleSelectPlan('premium')}
                            >
                                {t('pricing.startTrial')}
                            </button>
                            <span className="cta-note">{t('pricing.noCardRequired')}</span>
                        </div>
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default Pricing;
